import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AppUseDispatch, AppUseSelector } from '../../../redux/hooks'
import { SET_PLACEMENT, SET_PLACEMENT_DONE } from '../../../redux/types'
import { getPlacementExcerpt, submitPlacementPassage } from '../../../redux/actions/onboardingAction'
import { trackOnboarding } from '../../../utils/analytics'
import { hapticError } from '../../../utils/haptics'
import { converterWord } from '../../../utils/helperFunction'
import { characterConst } from '../../../utils/constant'
import { useWordAudio } from '../../../hooks/useWordAudio'
import { CheckIcon, DifficultyIcon } from './icons'
import { StepTitle, StepSubtitle } from '../stepContext'
import { localizedLevelLabel } from '../../story-card/constants'

// `label` is a translation key.
const RATINGS = [
  { key: 'easy' as const, bars: 1 as const, label: 'onboarding:placement.ratings.easy' },
  { key: 'ok' as const, bars: 2 as const, label: 'onboarding:placement.ratings.ok' },
  { key: 'hard' as const, bars: 3 as const, label: 'onboarding:placement.ratings.hard' },
]

const PlacementStep = ({ next }: { next: () => void }) => {
  const { t } = useTranslation('onboarding')
  const dispatch = AppUseDispatch()
  const { languageInfo, preferenceType, goal, examBand, placement, placementDone } = AppUseSelector(
    ({ onboarding }) => onboarding,
  )
  const [unknownIds, setUnknownIds] = useState<Set<string>>(new Set())
  const [showRating, setShowRating] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [chosenRating, setChosenRating] = useState<'easy' | 'ok' | 'hard' | null>(null)
  const [loading, setLoading] = useState(!placement || Boolean(placementDone))
  const [error, setError] = useState('')
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const startedAtRef = useRef<number>(Date.now())
  const { playWordAudio, stopCurrentAudio } = useWordAudio()

  const charPreference = preferenceType?.includes('Traditional') ? characterConst.traditional : characterConst.simplified
  const activePreferenceId = languageInfo?.preferences?.find((p: any) => p.is_active)?._id

  const fetchExcerpt = async (tempUser?: string, restart = false) => {
    setLoading(true)
    setError('')
    const data = await getPlacementExcerpt({
      language_id: languageInfo?._id,
      preference_id: activePreferenceId,
      temp_user: tempUser,
      exam_band_id: examBand?.band_id,
      learning_goal: goal || undefined,
      ...(restart ? { restart: true } : {}),
    })
    if (!data) {
      setError(t('placement.loadError'))
      setLoading(false)
      return
    }
    dispatch({
      type: SET_PLACEMENT,
      payload: {
        tempUser: data.temp_user,
        storyId: data.story_id,
        level: data.level,
        levelDisplay: data.level_display,
        passageIndex: data.passage_index,
        maxPassages: data.max_passages,
        audioUrl: data.audio_url,
        tokens: data.tokens,
      },
    })
    if (data.passage_index === 1) {
      trackOnboarding('placement_started', { start_level: data.level, exam_label: examBand?.label || 'none' })
    }
    setUnknownIds(new Set())
    setShowRating(false)
    setChosenRating(null)
    setLoading(false)
    // Scrolling happens in the effect below, once the passage has actually
    // rendered — see the note there.
  }

  useEffect(() => {
    if (languageInfo?._id) {
      if (placementDone) {
        // Re-entering a finished test (e.g. Back from the gate): the backend
        // session is done and rejects further submissions — restart it fresh
        // so the learner can retake instead of getting stuck on an error.
        dispatch({ type: SET_PLACEMENT_DONE, payload: null })
        fetchExcerpt(placement?.tempUser, true)
      } else if (!placement) {
        fetchExcerpt()
      }
    }
    return () => {
      audioRef.current?.pause()
      stopCurrentAudio()
    }
  }, [languageInfo?._id])

  // Bring the passage itself into view whenever a new one loads.
  //
  // This used to scroll the window to the top, which lands the user on the
  // group header — above the exam question they just answered — rather than on
  // the passage. It has to run after the passage has rendered, not when the
  // fetch resolves, or it measures the loading spinner's position instead.
  useEffect(() => {
    if (loading || !placement) return
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [placement?.passageIndex, placement?.storyId, loading])

  const uniqueDictWords = useMemo(() => {
    const seen = new Map<string, number | null>()
    placement?.tokens?.forEach((token) => {
      if (token.dict_id && !seen.has(token.dict_id)) seen.set(token.dict_id, token.frequency)
    })
    return seen
  }, [placement?.tokens])

  const toggleWord = (token: { dict_id: string | null; has_audio?: boolean }) => {
    const dictId = token.dict_id
    if (!dictId || showRating) return
    setUnknownIds((prev) => {
      const nextSet = new Set(prev)
      if (nextSet.has(dictId)) nextSet.delete(dictId)
      else nextSet.add(dictId)
      return nextSet
    })
    // Same pronunciation playback as tapping a word in the reader: male voice,
    // then female. Forced on here — pre-signup users have no audio setting yet.
    playWordAudio({ _id: dictId, has_audio: token.has_audio }, 'both')
  }

  const toggleAudio = () => {
    if (!placement?.audioUrl) return
    if (!audioRef.current) {
      audioRef.current = new Audio(placement.audioUrl)
      audioRef.current.onended = () => setIsPlaying(false)
    }
    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current.play().catch(() => setIsPlaying(false))
      setIsPlaying(true)
    }
  }

  const stopAudio = () => {
    audioRef.current?.pause()
    audioRef.current = null
    setIsPlaying(false)
    stopCurrentAudio()
  }

  const submitWithRating = async (rating: 'easy' | 'ok' | 'hard') => {
    // The finished test stays on screen above the group's Continue, so a
    // second tap on a rating is easy to make. It must not reach the
    // spent-session recovery below, which would restart the test.
    if (!placement || submitting || placementDone) return
    setSubmitting(true)
    setChosenRating(rating)
    stopAudio()

    const words = Array.from(uniqueDictWords.entries()).map(([dict_id, frequency]) => ({
      dict_id,
      frequency,
      known: !unknownIds.has(dict_id),
    }))

    const result = await submitPlacementPassage({
      temp_user: placement.tempUser,
      story_id: placement.storyId,
      words,
      rating,
    })

    if (!result) {
      // The commonest cause is a spent session: the backend closes a placement
      // run once it has converged and refuses further submissions against it,
      // and a passage left over in persisted state from an earlier run will
      // submit into exactly that. Retrying by hand can never fix it, so start a
      // fresh run rather than dead-end the signup on an error message.
      hapticError()
      setSubmitting(false)
      trackOnboarding('placement_submit_failed', { passage_index: placement.passageIndex })
      dispatch({ type: SET_PLACEMENT_DONE, payload: null })
      await fetchExcerpt(placement.tempUser, true)
      return
    }

    trackOnboarding('placement_passage_submitted', {
      passage_index: placement.passageIndex,
      level: placement.level,
      unknown_count: unknownIds.size,
      word_count: uniqueDictWords.size,
      rating,
    })

    if (result.done) {
      dispatch({
        type: SET_PLACEMENT_DONE,
        payload: {
          convergedLevel: result.converged_level,
          convergedLevelDisplay: result.converged_level_display,
          passagesDone: result.passages_done,
        },
      })
      trackOnboarding('placement_completed', {
        converged_level: result.converged_level,
        passages: result.passages_done,
        duration_seconds: Math.round((Date.now() - startedAtRef.current) / 1000),
      })
      setSubmitting(false)
      next()
    } else {
      setSubmitting(false)
      fetchExcerpt(placement.tempUser)
    }
  }

  if (loading) {
    return (
      <div className='ob-step ob-step--wide' ref={rootRef}>
        <div className='ob-loader'>
          <div className='ob-spinner' />
          <p className='ob-loader-title'>{t('placement.loading')}</p>
        </div>
      </div>
    )
  }

  if (error && !placement) {
    return (
      <div className='ob-step'>
        <div className='ob-loader'>
          <p className='ob-loader-title'>{t('placement.errorTitle')}</p>
          <p className='ob-loader-sub'>{error}</p>
          <button type='button' className='ob-cta' onClick={() => fetchExcerpt()}>
            {t('placement.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='ob-step ob-step--wide' ref={rootRef}>
      <StepTitle>{t('placement.title')}</StepTitle>
      <StepSubtitle>{t('placement.subtitle')}</StepSubtitle>

      <div className='ob-passage-meta'>
        <span className='ob-passage-level'>
          {t('placement.passage', {
            index: placement?.passageIndex ?? '',
            total: placement?.maxPassages ?? '',
            level: localizedLevelLabel(placement?.level, placement?.levelDisplay) ?? '',
          })}
        </span>
        {placement?.audioUrl ? (
          <button type='button' className={`ob-audio-btn ${isPlaying ? 'is-playing' : ''}`} onClick={toggleAudio}>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
              {isPlaying ? <path d='M6 5h4v14H6zM14 5h4v14h-4z' /> : <path d='M8 5v14l11-7z' />}
            </svg>
            {isPlaying ? t('placement.pauseAudio') : t('placement.listen')}
          </button>
        ) : null}
      </div>

      <div className='ob-passage-card'>
        <p className='ob-passage-text' lang='zh'>
          {placement?.tokens?.map((token, i) =>
            token.dict_id ? (
              <span
                key={`${token.dict_id}-${i}`}
                role='button'
                tabIndex={0}
                className={`ob-token ${unknownIds.has(token.dict_id) ? 'is-unknown' : ''}`}
                onClick={() => toggleWord(token)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggleWord(token)
                  }
                }}
              >
                {converterWord(token.word, charPreference)}
              </span>
            ) : token.word === '\n' ? (
              <br key={`br-${i}`} />
            ) : (
              <span key={`plain-${i}`} className='ob-token ob-token--plain'>
                {converterWord(token.word, charPreference)}
              </span>
            ),
          )}
        </p>

        <div className='ob-passage-footer'>
          <span className='ob-tap-count'>
            {unknownIds.size > 0 && (
              <Trans
                t={t}
                i18nKey='placement.markedCount'
                values={{ count: unknownIds.size }}
                components={{ strong: <strong /> }}
              />
            )}
          </span>
          {!showRating && (
            <button type='button' className='ob-cta' onClick={() => setShowRating(true)} disabled={submitting}>
              {t('nav.continue')}
            </button>
          )}
        </div>
      </div>

      {showRating && (
        <div className='ob-rating'>
          <p className='ob-rating-title'>{t('placement.ratingTitle')}</p>
          <div className='ob-rating-options'>
            {RATINGS.map((rating) => (
              <button
                key={rating.key}
                type='button'
                className={`ob-rating-btn ${chosenRating === rating.key ? 'is-selected' : ''}`}
                disabled={submitting || Boolean(placementDone)}
                onClick={() => submitWithRating(rating.key)}
              >
                <span className='ob-rating-icon'>
                  <DifficultyIcon level={rating.bars} />
                </span>
                {t(rating.label)}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && placement ? <p className='ob-error'>{error}</p> : null}

      <p className='ob-trust-line'>
        <span style={{ color: 'var(--ob-green)', lineHeight: 0 }}>
          <CheckIcon size={14} />
        </span>
        {t('placement.trustLine')}
      </p>
    </div>
  )
}

export default PlacementStep
